import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import Navigation from './components/Navigation';
import ScheduleGrid from './components/ScheduleGrid';
import Login from './components/Login';
import ChangePassword from './components/ChangePassword';
import NotificationContainer from './components/NotificationContainer';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import { useNotificationContext } from './contexts/NotificationContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import './components/PDFPages.css';
import StudentManagement from './components/StudentManagement';
import AbsenceManagement from './components/AbsenceManagement';

// Direct imports - plus de lazy loading
import Dashboard from './components/Dashboard';
import Sessions from './components/Sessions';
import Classes from './components/Classes';
import Teachers from './components/Teachers';
import Courses from './components/Courses';
import Rooms from './components/Rooms';
import Departments from './components/Departments';
import University from './components/University';
import AcademicYears from './components/AcademicYears';
import Grades from './components/Grades';
import Tracks from './components/Tracks';
import Users from './components/Users';
import Messages from './components/Messages';
import Planning from './components/Planning';
import Timetables from './components/Timetables';
import TimetableTest from './components/TimetableTest';
import PDFEmplois from './components/PDFEmplois';
import PDFEnseignants from './components/PDFEnseignants';
import PDFBilan from './components/PDFBilan';
import HomePage from './components/HomePage';
import StudentSpace from './components/StudentSpace';
import ContactPage from './components/ContactPage';
import AIScheduleGenerator from './components/AIScheduleGenerator';
import CourseImport from './components/CourseImport';
import TeacherSpace from './components/TeacherSpaceNew';
import AcademicYearConfig from './components/AcademicYearConfig';
import SEOHead from './components/SEOHead';

function App() {
  const [currentPage, setCurrentPage] = useState(() => {
    return localStorage.getItem('currentPage') || 'home';
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Mémoriser la fonction de toggle pour éviter les re-renders
  const toggleSidebar = React.useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);
  const [user, setUser] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const { notifications, removeNotification } = useNotificationContext();

  useEffect(() => {
    // Configure axios base URL
    const apiUrl = import.meta.env.VITE_API_URL || '';
    if (apiUrl) {
      axios.defaults.baseURL = apiUrl;
    }
    
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    localStorage.setItem('currentPage', currentPage);
  }, [currentPage]);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentPage');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setCurrentPage('home');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return (
          <ErrorBoundary>
            <SEOHead />
            <HomePage setCurrentPage={setCurrentPage} />
          </ErrorBoundary>
        );
      case 'student':
        return (
          <ErrorBoundary>
            <SEOHead 
              title="Espace Étudiant - Chronaxis"
              description="Consultez votre emploi du temps universitaire en ligne. Accès étudiant à la plateforme Chronaxis."
              canonical="https://chronaxis.tn/student"
            />
            <StudentSpace setCurrentPage={setCurrentPage} />
          </ErrorBoundary>
        );
      case 'teacher':
        return (
          <ErrorBoundary>
            <SEOHead 
              title="Espace Enseignant - Chronaxis"
              description="Gérez vos cours et consultez votre emploi du temps. Espace enseignant de la plateforme Chronaxis."
              canonical="https://chronaxis.tn/teacher"
            />
            <TeacherSpace setCurrentPage={setCurrentPage} />
          </ErrorBoundary>
        );
      case 'contact':
        return (
          <ErrorBoundary>
            <SEOHead 
              title="Contact - Chronaxis"
              description="Contactez l'équipe Chronaxis pour vos questions sur la gestion d'emplois du temps universitaires."
              canonical="https://chronaxis.tn/contact"
            />
            <ContactPage setCurrentPage={setCurrentPage} />
          </ErrorBoundary>
        );
      case 'admin':
        return user ? (
          <ErrorBoundary>
            <Dashboard setCurrentPage={setCurrentPage} />
          </ErrorBoundary>
        ) : <Login onLogin={handleLogin} setCurrentPage={setCurrentPage} />;
      case 'dashboard':
        return (
          <ErrorBoundary>
            <Dashboard setCurrentPage={setCurrentPage} />
          </ErrorBoundary>
        );
      case 'schedule':
        return (
          <ErrorBoundary>
            <ScheduleGrid />
          </ErrorBoundary>
        );
      case 'planning':
        return (
          <ErrorBoundary>
            <Planning />
          </ErrorBoundary>
        );
      case 'pdf':
      case 'pdf-emplois':
        return (
          <ErrorBoundary>
            <PDFEmplois />
          </ErrorBoundary>
        );
      case 'pdf-enseignants':
        return (
          <ErrorBoundary>
            <PDFEnseignants />
          </ErrorBoundary>
        );
      case 'pdf-bilan':
        return (
          <ErrorBoundary>
            <PDFBilan />
          </ErrorBoundary>
        );
      case 'sessions':
        return (
          <ErrorBoundary>
            <Sessions />
          </ErrorBoundary>
        );
      case 'timetables':
        return (
          <ErrorBoundary>
            <Timetables />
          </ErrorBoundary>
        );
      case 'teachers':
        return (
          <ErrorBoundary>
            <Teachers />
          </ErrorBoundary>
        );
      case 'classes':
        return (
          <ErrorBoundary>
            <Classes />
          </ErrorBoundary>
        );
      case 'courses':
        return (
          <ErrorBoundary>
            <Courses />
          </ErrorBoundary>
        );
      case 'studentManagement':
        return (
          <ErrorBoundary>
            <StudentManagement />
          </ErrorBoundary>
        );
      case 'absences':
        return (
          <ErrorBoundary>
            <AbsenceManagement />
          </ErrorBoundary>
        );
      case 'rooms':
        return (
          <ErrorBoundary>
            <Rooms />
          </ErrorBoundary>
        );
      case 'departments':
        return (
          <ErrorBoundary>
            <Departments />
          </ErrorBoundary>
        );
      case 'university':
        return (
          <ErrorBoundary>
            <University />
          </ErrorBoundary>
        );
      case 'academic-years':
        return (
          <ErrorBoundary>
            <AcademicYears />
          </ErrorBoundary>
        );
      case 'academic-year-config':
        return (
          <ErrorBoundary>
            <AcademicYearConfig />
          </ErrorBoundary>
        );
      case 'grades':
        return (
          <ErrorBoundary>
            <Grades />
          </ErrorBoundary>
        );
      case 'tracks':
        return (
          <ErrorBoundary>
            <Tracks />
          </ErrorBoundary>
        );
      case 'timetable-test':
        return (
          <ErrorBoundary>
            <TimetableTest />
          </ErrorBoundary>
        );
      case 'users':
        return (
          <ErrorBoundary>
            <Users />
          </ErrorBoundary>
        );
      case 'messages':
        return (
          <ErrorBoundary>
            <Messages />
          </ErrorBoundary>
        );
      case 'ai-schedule':
        return (
          <ErrorBoundary>
            <AIScheduleGenerator />
          </ErrorBoundary>
        );
      case 'course-import':
        return (
          <ErrorBoundary>
            <CourseImport />
          </ErrorBoundary>
        );
      default:
        return (
          <ErrorBoundary>
            <HomePage setCurrentPage={setCurrentPage} />
          </ErrorBoundary>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Handle navigation when user is not logged in
  if (!user && !['home', 'student', 'teacher', 'contact', 'admin'].includes(currentPage)) {
    setCurrentPage('home');
    return null; // Prevent rendering during redirect
  }
  
  if (!user && currentPage === 'admin') {
    return <Login onLogin={handleLogin} setCurrentPage={setCurrentPage} />;
  }

  // Public pages (home, student, teacher, contact) - no admin layout
  if (['home', 'student', 'teacher', 'contact'].includes(currentPage)) {
    return (
      <div className="min-h-screen bg-gray-50">
        {renderPage()}
        <NotificationContainer 
          notifications={notifications} 
          removeNotification={removeNotification} 
        />
      </div>
    );
  }

  // Admin pages - full admin layout
  return (
    <WebSocketProvider>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={toggleSidebar}
                title={sidebarOpen ? 'Masquer le menu' : 'Afficher le menu'}
              >
                {sidebarOpen ? '◀' : '▶'}
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Chronaxis</h1>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 hidden sm:block">
                Bonjour, {user?.firstName} {user?.lastName}
              </span>
              <button 
                onClick={() => setShowChangePassword(true)} 
                className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
              >
                Mot de passe
              </button>
              <button 
                onClick={handleLogout} 
                className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
              >
                Déconnexion
              </button>
            </div>
          </div>
        </header>
        <div className="flex flex-1 overflow-hidden">
          <button 
            className="lg:hidden fixed top-20 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
            onClick={() => setMobileNavOpen(prev => !prev)}
          >
            ☰
          </button>
          <Navigation 
            currentPage={currentPage} 
            setCurrentPage={setCurrentPage}
            mobileNavOpen={mobileNavOpen}
            setMobileNavOpen={setMobileNavOpen}
            sidebarOpen={sidebarOpen}
            user={user}
          />
          <main className="flex-1 overflow-auto">
            {renderPage()}
          </main>
        </div>
      {showChangePassword && (
        <ChangePassword onClose={() => setShowChangePassword(false)} />
      )}
      <NotificationContainer 
        notifications={notifications} 
        removeNotification={removeNotification} 
      />
      </div>
    </WebSocketProvider>
  );
}

export default App;
